// The hotbar's action catalogue (10 §4 vocabulary). `catalogueFromWasm` builds it from
// `fk-sim-wasm.abilities()`; the arg schema per ability follows what `fk_sim::legal` accepts
// for the ability's `area` (engine.rs): SelfUnit → none, or tile when the ability moves the
// caster; Single → unit (unitDir when it shoves, tile when it lays a trap); Blast → tile;
// Line → line; Adjacent / AlliesWithin → none. Legality itself always comes from `legal()`.
import type { ActionArgs, ActionId, Slot, UnitState } from './types';
import { forUnit } from './weapon';
import { describeAbility } from './describe';
import { abilityItem, abilitySet, abilityUpgrade, catalogueItem, fightUse } from '../items/catalogue';

export type ArgSchema = ActionArgs['kind'];
export type Glyph = 'move' | 'attack' | 'beam' | 'area' | 'shield' | 'heal' | 'ward' | 'guard' | 'interact' | 'neutral';

export interface ActionDef {
  id: ActionId;
  name: string;
  args: ArgSchema;
  /** docs/115 ME5: a single aim that may also land on a bare square (a flask, a frost ray at a puddle). */
  ground?: boolean;
  range: number;            // Manhattan tiles (docs/39) as the catalogue states it (Reach = 1, SelfOnly = 0; Move = the unit's move)
  slots: readonly Slot[];   // which slots may hold it
  amount?: number;          // catalogue figure shown as a hint on the hotbar (never on the board)
  glyph: Glyph;
  hotkey: string;
  className?: string;
  passive?: boolean;
  requires?: string[];
  cooldown?: number;
  cost?: number;
  perAdventure?: number;
  /** What it does in plain words (the hotbar's tooltip, `describeAbility`). */
  text?: string;
  /** Deals its wielder's weapon damage: past reach it is a missile the weather and wind act on (docs/26 F6, docs/29 X2). */
  missile?: boolean;
  /** docs/72 IT18: an item's ability (`10000 + def`): drawn with the piece's kit icon (docs/129); `used` = spent
   *  from the bag. `glyph` is an Oryx frame drawn instead when it stands for no piece (an upgrade's ability, `def` 0). */
  item?: { def: number; glyph?: string; used: boolean };
  /** Its damage as the rules make it (`fk_grammar::Dmg`): the weapon's (plus n), a spell's base, or flat. */
  dmg?: { kind: 'Weapon' | 'WeaponPlus' | 'Spell' | 'Flat'; n: number };
  /** `DamageMul`: the weapon damage multiplied (a Backstab). */
  mul?: number;
  /** A cast from where the hero stands onto the squares round it (`Area::Adjacent` = 1, `AlliesWithin(n)` = n): a Finale, a War Drum, a Bloom. */
  aura?: number;
  /** How its range is measured (`RangeMax`): the main hand's, melee reach, a fixed count, or self. */
  reach?: 'Weapon' | 'Reach' | 'Tiles' | 'SelfOnly';
  /** The hero's own weapon, drawn by its kit icon, for a weapon attack (`weapon.ts` `forUnit`). */
  weaponIcon?: { def: number; name: string };
}

export const HOLD: ActionDef = { id: 2, name: 'Hold', args: 'none', range: 0, slots: [1, 2, 'B'], glyph: 'neutral', hotkey: '0', text: 'Do nothing with this slot (the hero stands ready).' };
/** docs/83 PO8: Change Gear (`fk_grammar` ability 8), only ever planned from the inventory. */
export const CHANGE_GEAR = 8;
/** docs/93 AQ8: Lend a Hand (`fk_grammar` ability 9), offered only beside a floundering friend. */
export const LEND_A_HAND = 9;
/** docs/155 WB1: Break Free (`fk_grammar` ability 17), offered only while you or a friend beside you is caught in a web. */
export const BREAK_FREE = 17;
export const MOVE: ActionDef = { id: 1, name: 'Move', args: 'tile', range: 4, slots: [1, 2], glyph: 'move', hotkey: '1', text: 'Walk to a square you can reach this turn. Rough ground costs more; foes next to you hold you up.' };

/** Raw `fk_grammar::AbilityDef` as serde-wasm-bindgen hands it over. */
export interface WasmAbility {
  id: number; name: string; class?: string; level_req: number;
  slot: 'Action' | 'Bonus' | 'Either' | 'Passive';
  range: { min: number; max: 'SelfOnly' | 'Reach' | 'Weapon' | { Tiles: number }; los: boolean };
  area: 'Single' | 'SelfUnit' | 'Adjacent' | { Blast: { radius: number } } | { Line: { len: number } } | { AlliesWithin: number };
  effects: unknown[];
  cooldown: number; resource_cost: number; per_pulse: number; per_adventure: number;
  requires: unknown[];
}

const variant = (v: unknown): string => (typeof v === 'string' ? v : v && typeof v === 'object' ? Object.keys(v as object)[0] ?? '' : '');
const payload = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? ((v as Record<string, unknown>)[variant(v)] as Record<string, unknown>) ?? {} : {});

function slotsOf(s: WasmAbility['slot']): readonly Slot[] {
  switch (s) {
    case 'Action': return [1, 2];
    case 'Bonus': return ['B'];
    case 'Either': return [1, 2, 'B'];
    case 'Passive': return [];
  }
}

function argsOf(a: WasmAbility): ArgSchema {
  const kinds = a.effects.map(variant);
  const area = variant(a.area);
  switch (area) {
    case 'SelfUnit':
      if (a.id === 6) return 'item';                // Use Item: a bag row, maybe a friend (docs/40)
      return a.id === 1 || kinds.includes('MoveSelf') || kinds.includes('Teleport') ? 'tile' : 'none';
    case 'Single':
      if (a.id === 3) return 'interact';            // Interact: a fixture + verb (docs/25: open a stash)
      if (a.id === LEND_A_HAND) return 'unit';      // docs/93 AQ8: the sim picks the bank (`bank_for`), no side to choose
      if (a.id === BREAK_FREE) return 'unit';       // docs/155 WB1: yourself or a friend beside you
      if (kinds.includes('Trap') || kinds.includes('Disarm')) return 'tile';   // a Snare's square; docs/52 a trap to disarm
      if (kinds.includes('Teleport')) return 'tile';  // an item's blink (Wayfarer's Blinking Helm): the square it lands on
      // a shove goes the way the player picks; a pull always drags toward the caster (engine.rs)
      if (kinds.includes('Push')) return 'unitDir';
      return 'unit';
    case 'Blast': return 'tile';
    case 'Line': return 'line';
    default: return 'none';                        // Adjacent, AlliesWithin
  }
}

function glyphOf(a: WasmAbility, args: ArgSchema): Glyph {
  const kinds = a.effects.map(variant);
  if (a.id === 2) return 'neutral';
  if (a.id === 6) return 'ward';
  if (a.id === LEND_A_HAND) return 'heal';           // docs/93 AQ8: a hand for a friend, aimed like a mend
  if (a.id === BREAK_FREE) return 'heal';            // docs/155 WB1: a friend cut loose, aimed like a mend
  if (a.id === 1 || (kinds.includes('MoveSelf') && !kinds.includes('Damage')) || kinds.includes('Teleport') || kinds.includes('MoveBonus')) return 'move';
  const area = variant(a.area);
  if (kinds.includes('Damage') && (area === 'Blast' || area === 'Line' || area === 'Adjacent')) return 'area';
  if (kinds.includes('HazardTiles') || kinds.includes('Grow')) return 'area';   // docs/104 DR2: a druid's Grow changes the ground
  if (kinds.includes('Damage')) return variant(a.range.max) === 'Tiles' || variant(a.range.max) === 'Weapon' ? 'beam' : 'attack';
  if (kinds.includes('Heal') || kinds.includes('Revive')) return 'heal';
  const status = a.effects.find((e) => variant(e) === 'Status');
  if (status) {
    const p = payload(status);
    if (p['kind'] === 'Warded') return 'ward';
    if (p['kind'] === 'Guarded') return p['to'] === 'Caster' ? 'guard' : 'shield';
    if (p['kind'] === 'Blessed') return 'ward';
  }
  if (args === 'tile' || args === 'raw' || args === 'interact') return 'interact';
  return 'interact';
}

function rangeOf(a: WasmAbility): number {
  const m = a.range.max;
  if (typeof m === 'object') return m.Tiles;
  if (m === 'Reach' || m === 'Weapon') return 1;
  if (a.id === 1) return 4;
  const mv = a.effects.find((e) => variant(e) === 'MoveSelf' || variant(e) === 'Teleport');
  return mv ? Number(payload(mv)['tiles'] ?? 0) : 0;
}

function amountOf(a: WasmAbility): number | undefined {
  for (const e of a.effects) {
    const p = payload(e);
    switch (variant(e)) {
      case 'Heal': return Number(p['amount']);
      case 'MoveBonus': return Number(p['tiles']) || undefined;   // a Dash: the squares it adds
      case 'Status': return Number(p['magnitude']) || undefined;
      case 'Damage': {
        const d = p['dmg'];
        if (typeof d === 'object' && d) { const k = variant(d); const v = (d as Record<string, number>)[k]; return k === 'Spell' || k === 'Flat' ? v : undefined; }
        return undefined;
      }
    }
  }
  return undefined;
}

function dmgOf(a: WasmAbility): ActionDef['dmg'] {
  const e = a.effects.find((x) => variant(x) === 'Damage');
  if (!e) return undefined;
  const d = payload(e)['dmg'];
  const k = variant(d);
  if (k !== 'Weapon' && k !== 'WeaponPlus' && k !== 'Spell' && k !== 'Flat') return undefined;
  return { kind: k, n: typeof d === 'object' && d ? Number((d as Record<string, unknown>)[k]) || 0 : 0 };
}

export function catalogueFromWasm(list: WasmAbility[]): ActionDef[] {
  return list.map((a) => {
    const args = argsOf(a);
    const def: ActionDef = {
      id: a.id, name: a.name, args, range: rangeOf(a), slots: slotsOf(a.slot), glyph: glyphOf(a, args), hotkey: '',
      passive: a.slot === 'Passive', requires: a.requires.map(variant), cooldown: a.cooldown, cost: a.resource_cost,
    };
    if (a.class) def.className = a.class;
    if (a.per_adventure) def.perAdventure = a.per_adventure;
    def.text = describeAbility(a);
    if (a.effects.some((e) => variant(e) === 'Damage' && ['Weapon', 'WeaponPlus'].includes(variant(payload(e)['dmg'])))) def.missile = true;
    const dmg = dmgOf(a);
    if (dmg) def.dmg = dmg;
    const mul = a.effects.find((e) => variant(e) === 'DamageMul');
    if (mul) def.mul = Number(payload(mul)['num']) || 1;
    def.reach = variant(a.range.max) as NonNullable<ActionDef['reach']>;
    if (a.area === 'Adjacent') def.aura = 1;
    else if (variant(a.area) === 'AlliesWithin') def.aura = Number((a.area as { AlliesWithin: number }).AlliesWithin) || 1;
    const amount = amountOf(a);
    if (amount !== undefined) def.amount = amount;
    const it = abilityItem(a.id);
    if (it) def.item = { def: it.id, used: !!fightUse(it) && !it.active };
    // docs/110: a set's ability shows its first piece on the hotbar
    const set = abilitySet(a.id);
    const head = set && catalogueItem(set.members[0]!);
    if (head) def.item = { def: head.id, used: false };
    // docs/112: an upgrade's ability shows an orb for an enchantment, a hammer for an improvement
    const up = abilityUpgrade(a.id);
    if (up) def.item = { def: 0, glyph: up.up.kind === 'enchant' ? 'item_092' : 'item_164', used: false };
    return def;
  });
}

let catalogue: ActionDef[] = [MOVE, HOLD];
const byId = new Map<number, ActionDef>(catalogue.map((a) => [a.id, a]));

export function installCatalogue(defs: ActionDef[]): void {
  catalogue = defs;
  byId.clear();
  for (const d of defs) byId.set(d.id, d);
}

export function actionById(id: ActionId): ActionDef | undefined { return byId.get(id); }
/** An action as `unit` has it (Basic Attack named for its weapon, `weapon.ts`). */
export function actionFor(id: ActionId, unit: UnitState | undefined): ActionDef | undefined { const d = byId.get(id); return d && forUnit(d, unit); }
export function allActions(): readonly ActionDef[] { return catalogue; }

const HOTKEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

const floundering = (u: UnitState | undefined): boolean => !!u?.statuses?.some((s) => s.kind === 'Floundering');

/**
 * docs/93 AQ8: what the water leaves on a hero's bar. Floundering, a hero may only take one step
 * out or hold (the sim's `IllegalReason::Floundering`), so the bar is Move and Hold; Lend a Hand
 * shows only while a friend touching the hero's body is floundering. `board` is every unit on it
 * (without it Lend a Hand stays hidden).
 */
export function waterBar(ids: readonly number[], unit: UnitState | undefined, board: readonly UnitState[] | undefined): number[] {
  if (floundering(unit)) return ids.filter((id) => id === MOVE.id || id === HOLD.id);
  // docs/155 WB1: Break Free shows only while there is a web to break
  const out = ids.includes(BREAK_FREE) && !breakFreeOffered(unit, board) ? ids.filter((id) => id !== BREAK_FREE) : [...ids];
  if (!out.includes(LEND_A_HAND)) return out;
  return lendAHandOffered(unit, board) ? out : out.filter((id) => id !== LEND_A_HAND);
}

/** The gap between two bodies on the four-way grid (the sim's `Footprint::gap_to`): 0 = the same body, 1 = touching on a side. */
function gap(a: UnitState, b: UnitState): number {
  const sa = a.size ?? 1, sb = b.size ?? 1;
  const gx = Math.max(0, a.tile.x - (b.tile.x + sb - 1), b.tile.x - (a.tile.x + sa - 1));
  const gy = Math.max(0, a.tile.y - (b.tile.y + sb - 1), b.tile.y - (a.tile.y + sa - 1));
  return gx + gy;
}

/** docs/155 WB1: held fast by a web (the sim's `Sim::webbed`: a web roots whoever stands in it each turn). */
const webbed = (u: UnitState | undefined): boolean => !!u?.statuses?.some((s) => s.kind === 'Rooted' || s.kind === 'Stuck');

/** docs/155 WB1: `unit`, or a friend touching it on a side, is caught in a web. */
export function breakFreeOffered(unit: UnitState | undefined, board: readonly UnitState[] | undefined): boolean {
  if (!unit || unit.downed || unit.onBoard === false) return false;
  if (webbed(unit)) return true;
  return !!board?.some((f) => f.unitId !== unit.unitId && f.onBoard !== false && !f.downed && f.faction !== 'enemy' && webbed(f) && gap(unit, f) === 1);
}

/** docs/93 AQ8: a friend beside `unit` (touching its body on a side) is floundering. */
export function lendAHandOffered(unit: UnitState | undefined, board: readonly UnitState[] | undefined): boolean {
  if (!unit || !board || unit.downed || unit.onBoard === false) return false;
  const near = (a: UnitState, b: UnitState): boolean => {
    const sa = a.size ?? 1, sb = b.size ?? 1;
    // the gap between two footprints on the four-way grid (the sim's `Footprint::gap_to` for bodies): 1 = touching on a side
    const gx = Math.max(0, a.tile.x - (b.tile.x + sb - 1), b.tile.x - (a.tile.x + sa - 1));
    const gy = Math.max(0, a.tile.y - (b.tile.y + sb - 1), b.tile.y - (a.tile.y + sa - 1));
    return gx + gy === 1;
  };
  return board.some((f) => f.unitId !== unit.unitId && f.onBoard !== false && !f.downed && f.faction !== 'enemy' && floundering(f) && near(unit, f));
}

/**
 * A hero's hotbar for one slot: its `stats.abilities` that may sit in that slot (per-slot legality
 * by the catalogue's `slot`; `legal()` decides the rest once a target is chosen). Hold is always
 * last on `0`; Move is `1` when present.
 */
export function actionsForHero(abilities: readonly number[] | undefined, slot: Slot, unit?: UnitState, board?: readonly UnitState[]): ActionDef[] {
  const ids = waterBar(abilities && abilities.length ? abilities : [1, 2, 5], unit, board);
  const defs = ids.map((id) => byId.get(id)).filter((d): d is ActionDef => !!d && !d.passive && d.slots.includes(slot) && d.id !== 2 && d.args !== 'raw');
  const out: ActionDef[] = [];
  let k = 0;
  for (const d of defs) out.push({ ...forUnit(d, unit), hotkey: HOTKEYS[k++] ?? '' });
  out.push({ ...HOLD, hotkey: '0' });
  return out;
}
